import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import { useEntities } from '../hooks/useEntities';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import FilterBar from '../components/ui/FilterBar';

const API = `${API_V1}/gl/profitability`;

const DIMENSIONS = [
  { value: 'COST_CENTER', label: 'Cost Center' },
  { value: 'PROJECT', label: 'Project' },
  { value: 'ACTIVITY', label: 'Activity' },
  { value: 'REGION', label: 'Region' },
];

interface ProfitRow {
  dimension_value: string;
  revenue: number;
  cogs: number;
  gross_profit: number;
  opex: number;
  net_income: number;
  margin_pct: number;
}

interface ProfitData {
  dimension: string;
  entity_code: string | null;
  period: string | null;
  rows: ProfitRow[];
  summary: ProfitRow;
}

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const marginColor = (pct: number) => {
  if (pct >= 20) return '#16a34a';
  if (pct >= 10) return '#ca8a04';
  return '#dc2626';
};

const thStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '10px 14px', color: 'var(--text-hint)', borderBottom: '2px solid var(--border-subtle)' };
const tdStyle: React.CSSProperties = { fontSize: 13, padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)' };

export default function ProfitabilityAnalysis() {
  const entities = useEntities();
  const [entityFilter, setEntityFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [dimension, setDimension] = useState('COST_CENTER');
  const [data, setData] = useState<ProfitData | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ dimension });
    if (entityFilter) p.set('entity_code', entityFilter);
    if (periodFilter) p.set('period', periodFilter);
    try {
      const res = await authFetch(`${API}?${p}`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [entityFilter, periodFilter, dimension]);

  useEffect(() => { load(); }, [load]);

  return (
    <PageLayout title="Profitability Analysis" subtitle="P&L breakdown by cost center, project, or other dimensions">

      <FilterBar filters={[
        {
          key: 'dimension', label: 'Dimension', type: 'select', value: dimension,
          onChange: setDimension,
          options: DIMENSIONS.map(d => ({ value: d.value, label: d.label })),
          placeholder: 'Select Dimension',
        },
        {
          key: 'entity', label: 'Entity', type: 'select', value: entityFilter,
          onChange: setEntityFilter,
          options: entities.map(e => ({ value: e.entity_code, label: `${e.entity_code} - ${e.entity_name}` })),
        },
        {
          key: 'period', label: 'Period', type: 'search', value: periodFilter,
          onChange: setPeriodFilter, width: 140, placeholder: 'YYYY-MM',
        },
      ]} />

      {/* Summary KPIs */}
      {data?.summary && (
        <KpiGrid items={[
          { label: 'Total Revenue', value: formatAmountCompact(data.summary.revenue), color: 'var(--positive)' },
          { label: 'COGS', value: formatAmountCompact(data.summary.cogs), color: 'var(--text-secondary)' },
          { label: 'Gross Profit', value: formatAmountCompact(data.summary.gross_profit), color: 'var(--accent)' },
          { label: 'OpEx', value: formatAmountCompact(data.summary.opex), color: 'var(--warning)' },
          { label: 'Net Income', value: formatAmountCompact(data.summary.net_income), color: data.summary.net_income >= 0 ? 'var(--positive)' : 'var(--negative)' },
          { label: 'Margin', value: `${data.summary.margin_pct.toFixed(1)}%`, color: marginColor(data.summary.margin_pct) },
        ]} columns={6} />
      )}

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-hint)' }}>Loading...</div>
      ) : (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Dimension', 'Revenue', 'COGS', 'Gross Profit', 'OpEx', 'Net Income', 'Margin %'].map(h => (
                  <th key={h} style={{ ...thStyle, textAlign: h === 'Dimension' ? 'left' : 'right' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map(row => (
                <tr key={row.dimension_value}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{row.dimension_value}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(row.revenue)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text-secondary)' }}>{fmt(row.cogs)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(row.gross_profit)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text-secondary)' }}>{fmt(row.opex)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: row.net_income >= 0 ? 'var(--positive)' : 'var(--negative)' }}>{fmt(row.net_income)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                      color: marginColor(row.margin_pct),
                      background: row.margin_pct >= 20 ? '#dcfce7' : row.margin_pct >= 10 ? '#fef9c3' : '#fee2e2',
                    }}>
                      {row.margin_pct.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
              {(data?.rows || []).length === 0 && (
                <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: 'var(--text-hint)' }}>No profitability data available for the selected filters</td></tr>
              )}
            </tbody>
            {data?.summary && (data?.rows || []).length > 0 && (
              <tfoot>
                <tr style={{ background: 'var(--bg-elevated)', borderTop: '2px solid var(--border-default)', fontWeight: 700 }}>
                  <td style={{ ...tdStyle }}>TOTAL</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(data.summary.revenue)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(data.summary.cogs)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(data.summary.gross_profit)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(data.summary.opex)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: data.summary.net_income >= 0 ? 'var(--positive)' : 'var(--negative)' }}>{fmt(data.summary.net_income)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                      color: marginColor(data.summary.margin_pct),
                      background: data.summary.margin_pct >= 20 ? '#dcfce7' : data.summary.margin_pct >= 10 ? '#fef9c3' : '#fee2e2',
                    }}>
                      {data.summary.margin_pct.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </PageLayout>
  );
}
